import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Input, InputProps, InputStyled } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';
import { GmapsAddress, placeToAddress, useGoogleMap } from '/imports/gmaps';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';

const AddressInputStyled = styled(InputStyled)`
  position: relative;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  span {
    position: relative;
    flex: 1;
    &.line {
      flex: 1;
    }
    &.state {
      flex: 0.5;
    }
    &.line2 {
      flex: 0.5;
    }
    &.zip {
      flex: 0.5;
    }
    &.break {
      flex-basis: 100%;
      height: 0;
    }
  }
  .suggestions {
    position: absolute;
    background: #fff;
    z-index: 1;
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    .prediction {
      padding: 0.25rem 1rem;
      cursor: pointer;
      &:hover {
        background: ${css.grey7};
      }
    }
  }
`;

export interface AddressInputProps extends InputProps<GmapsAddress> {
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: GmapsAddress;
  onInput?(input?: string): void;
  onChange?(address?: GmapsAddress): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  autoCompleteRequest?: Partial<google.maps.places.AutocompletionRequest>;
  inline?: boolean;
}

/**
 * Address field with Google Places autocomplete; inline single-line or expanded multi-field layout.
 */
export const AddressInput = (props: AddressInputProps) => {
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>();
  const [address, setAddress] = useState<Partial<GmapsAddress>>(props.value || {});
  /**
   * Clears the input value and notifies via onClear/onInput.
   */
  const clearInput = () => {
    props.onClear?.();
    props.onInput?.(undefined);
    const input = inputRef.current;
    if (input) {
      input.value = '';
    }
  };
  const { isLoaded } = useGoogleMap();
  /**
   * Fetches Places autocomplete suggestions for the typed input.
   * @param input - current text query
   */
  const getSuggestions = async (input: string) => {
    if (!isLoaded) return;
    const options = {
      input,
      ...props.autoCompleteRequest,
    };
    const result = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(options as google.maps.places.AutocompleteRequest);
    setSuggestions(result?.suggestions);
  };
  /**
   * Resolves a chosen suggestion into a full address and updates state.
   * @param suggestion - the selected autocomplete suggestion
   */
  const placeSelected = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    const placeId = suggestion.placePrediction?.placeId;
    if (!placeId) return clearInput();
    const place = new google.maps.places.Place({ id: placeId });
    await place.fetchFields({ fields: ['addressComponents', 'displayName', 'location'] });
    const address = placeToAddress(place);
    delete address.place;
    setAddress(address);
    setSuggestions(undefined);
    if (inputRef.current) inputRef.current.value = props.inline ? address.formatted : address.line;
  };
  useEffect(() => {
    if (!isLoaded) return;
    address && props.onChange?.(address as GmapsAddress);
  }, [address]);
  return (
    <AddressInputStyled id={props.id} className={`input address-input ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {
        props.inline ?
          <span className="line">
            {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
            <input
              ref={inputRef}
              className={hasStartIcon ? 'has-start-icon' : ''}
              type="text"
              placeholder={props.placeholder || strings.Line()}
              disabled={props.disabled}
              autoFocus={props.autoFocus}
              defaultValue={address?.formatted}
              onChange={(e) => { props.onInput?.(e.target.value); getSuggestions(e.target.value); }}
              onClick={(e) => props.onClick?.(e)}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              onKeyUp={(e) => {
                props.onKeyPress?.(e);
                if (e.key === 'Enter') {
                  const suggestion = suggestions?.[0];
                  suggestion && placeSelected(suggestion);
                }
              }}
              autoComplete={props.autoComplete}
            />
            {
              props.value !== undefined &&
              !props.disabled &&
              !(props.type === 'color') &&
              <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
            }
            {
              suggestions?.length ?
                <div className="suggestions">
                  {
                    suggestions.map((p) => {
                      return <div key={p.placePrediction?.placeId} className="prediction" onClick={() => placeSelected(p)}>{p.placePrediction?.text.text}</div>;
                    })
                  }
                </div>
                :
                null
            }
          </span>
          :
          <>
            <span className="line">
              {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
              <input
                ref={inputRef}
                className={hasStartIcon ? 'has-start-icon' : ''}
                type="text"
                placeholder={props.placeholder || strings.Line()}
                disabled={props.disabled}
                autoFocus={props.autoFocus}
                defaultValue={address?.line}
                onChange={(e) => { props.onInput?.(e.target.value); getSuggestions(e.target.value); }}
                onClick={(e) => props.onClick?.(e)}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                onKeyUp={(e) => {
                  props.onKeyPress?.(e);
                  if (e.key === 'Enter') {
                    const suggestion = suggestions?.[0];
                    suggestion && placeSelected(suggestion);
                  }
                }}
                autoComplete={props.autoComplete}
              />
              {
                props.value !== undefined &&
                !props.disabled &&
                !(props.type === 'color') &&
                <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
              }
              {
                suggestions?.length ?
                  <div className="suggestions">
                    {
                      suggestions.map((p) => {
                        return <div key={p.placePrediction?.placeId} className="prediction" onClick={() => placeSelected(p)}>{p.placePrediction?.text.text}</div>;
                      })
                    }
                  </div>
                  :
                  null
              }
            </span>
            <span className="line2">
              <Input value={address?.line2} onChange={(line2) => setAddress({ ...address, line2 })} placeholder={strings.Line2()} />
            </span>
            <span className="break" />
            <span className="city">
              <Input value={address?.city} onChange={(city) => setAddress({ ...address, city })} placeholder={strings.City()} />
            </span>
            <span className="state">
              <Input value={address?.state} onChange={(state) => setAddress({ ...address, state })} placeholder={strings.State()} />
            </span>
            <span className="zip">
              <Input value={address?.zipcode} onChange={(zipcode) => setAddress({ ...address, zipcode })} placeholder={strings.Zipcode()} />
            </span>
          </>
      }
    </AddressInputStyled>
  );
};